import React, { useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon } from 'lucide-react';
import { questions } from '../data/quiz';
import { results } from '../data/results';
import { dominantLevel } from '../utils/scoring';
import type { LevelKey } from '../types/quiz';
import { ProgressBar } from './ProgressBar';
import { QuestionCard } from './QuestionCard';
import { ResultPanel } from './ResultPanel';
type Phase = 'questions' | 'result';
interface QuizExperienceProps {
  webinarUrl: string;
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function QuizExperience({
  webinarUrl
}: QuizExperienceProps) {
  const [phase, setPhase] = useState<Phase>('questions');
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, LevelKey>>({});
  const advanceTimer = useRef<number | null>(null);
  const question = questions[index];
  const orderedAnswers = questions.map(item => answers[item.id]).filter((value): value is LevelKey => Boolean(value));
  const result = results[dominantLevel(orderedAnswers)];
  function handleSelect(key: LevelKey) {
    setAnswers(prev => ({
      ...prev,
      [question.id]: key
    }));
    if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    advanceTimer.current = window.setTimeout(() => {
      if (index < questions.length - 1) {
        setIndex(prev => prev + 1);
      } else {
        setPhase('result');
      }
    }, 220);
  }
  function handleBack() {
    if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    setIndex(prev => Math.max(0, prev - 1));
  }
  function handleRestart() {
    setAnswers({});
    setIndex(0);
    setPhase('questions');
  }
  return <section id="test" className="w-full bg-hueso" aria-label="Test de nivel de consciencia" data-id="element-107" data-mp="sg1zdqthl5rr">
      <div className="mx-auto w-full max-w-lectura px-6 py-16 sm:py-20" data-id="element-108" data-mp="2quwlr3ykh5k7">
        {phase === 'questions' && <ProgressBar current={index + 1} total={questions.length} data-id="element-109" data-mp="2msrq5zok5yux" />}

        <div className={phase === 'questions' ? 'mt-10' : ''} data-id="element-110" data-mp="k6cmplx87ahl">
          <AnimatePresence mode="wait" initial={false} data-id="element-111" data-mp="k51nlzeq6qwt">
            <motion.div key={phase === 'questions' ? `q-${question.id}` : 'result'} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.25,
            ease: EASE
          }} data-id="element-112" data-mp="oxcksui2zylo">
              {phase === 'questions' ? <QuestionCard question={question} selected={answers[question.id]} onSelect={handleSelect} data-id="element-113" data-mp="12urfoer3syl1" /> : <ResultPanel result={result} webinarUrl={webinarUrl} onRestart={handleRestart} data-id="element-114" data-mp="tya274v9oom4" />}
            </motion.div>
          </AnimatePresence>
        </div>

        {phase === 'questions' && index > 0 && <button type="button" onClick={handleBack} className="mt-10 inline-flex items-center gap-2 font-sans text-sm text-noche/55 transition-colors duration-150 ease-out hover:text-violeta focus:outline-none focus-visible:ring-2 focus-visible:ring-violeta" data-id="element-115" data-mp="3jlho12qfgfk6">
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-116" data-mp="3l2euv3507uxz" />
            Volver a la pregunta anterior
          </button>}
      </div>
    </section>;
}